import * as React from "react";
import isEmpty from "lodash/isEmpty";
import { Context } from "@grandvisionhq/carts-v2";
import { toast } from "@grandvisionhq/common";
import { useLink } from "@grandvisionhq/frk-routing";
import { useSync } from "@grandvisionhq/lib-utils-w3c";
import { useIntl, useSelector, useDispatch } from "@grandvisionhq/state";
import { updateCartState } from "../../actions.js";
import { useRestoreCartShippingMethod } from "../../hooks/carts/hooks/carts/use-restore-cart-shipping-method.js";
import { useSetCustomerDetails } from "../../hooks/operations/hooks/operations/use-set-customer-details.js";
import { getServiceStore } from "../../selectors.js";
import { selectShippingMethod, selectStore, selectPickupPoint } from "../../StepShipping/StepShipping/actions.js";
import { useShippingMethodSelection } from "../../StepShipping/modules/ShippingMethods/hooks/StepShipping/modules/ShippingMethods/hooks/use-shipping-method-selection.js";
import { useShippingMethodsList } from "../../StepShipping/modules/ShippingMethods/hooks/StepShipping/modules/ShippingMethods/hooks/use-shipping-methods-list.js";
import { shippingMethodsFailed } from "../../StepShipping/modules/ShippingMethods/state/StepShipping/modules/ShippingMethods/state/actions.js";
import { isShippingStepValid, getSelectedShippingMethod, shouldValidateServiceStore, getSelectedStore, getSelectedPickupPoint } from "../../StepShipping/StepShipping/selectors.js";
import { usePrepareCustomerDetails } from "../hooks/StepOverview/hooks/use-prepare-customer-details.js";
import { StepOverview } from "./StepOverview.js";
import { ShippingMethod } from "../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { CheckoutStepErrorType } from "../../models/checkout/models/checkout/checkout-step-error-type.js";
import { useCheckoutSettings } from "../../context/CheckoutSettings/context/CheckoutSettings/context.js";
import { isPickupStore, isPickupPoint } from "../../utils/utils/shipping-method.js";
const StepOverviewContainer = (props) => {
  const { onNextStep, onPrevStep, shippingMethodsAsides } = props;
  const { getLabel } = useIntl();
  const { customerDetails } = usePrepareCustomerDetails();
  const isShippingStepValid$1 = useSelector(isShippingStepValid);
  const serviceStore = useSelector(getServiceStore);
  const { serviceStore: serviceStoreOptions, features } = useCheckoutSettings();
  const { cart, called, loading } = Context.useCart();
  const selectedShippingMethod = useSelector(getSelectedShippingMethod);
  const isPickupStore$1 = isPickupStore(selectedShippingMethod?.method);
  const validateServiceStore = useSelector(shouldValidateServiceStore);
  const selectedStore = useSelector(getSelectedStore);
  const selectedPickupPoint = useSelector(getSelectedPickupPoint);
  const { onClick: navigateToCart } = useLink({ route: "cart.overview" }, { replace: true }) || {};
  const broadCastChannel = useSync("new-cart-created").getChannel();
  const dispatch = useDispatch();
  const isPickupPoint$1 = isPickupPoint(selectedShippingMethod?.method);
  const { restoreShippingMethod } = useRestoreCartShippingMethod();
  broadCastChannel.onmessage = () => {
    toast.failed("cart.errors.cartNotInActiveState");
    navigateToCart?.();
  };
  const isOverviewStepValid = React.useMemo(() => {
    if (validateServiceStore && features?.isServiceStoreEnabled && !(serviceStoreOptions?.isOptional ?? true) && !isPickupStore$1 && !serviceStore) {
      return false;
    }
    return isShippingStepValid$1;
  }, [
    validateServiceStore,
    features?.isServiceStoreEnabled,
    isPickupStore$1,
    serviceStore,
    serviceStoreOptions?.isOptional,
    isShippingStepValid$1
  ]);
  const onShippingMethodLoadError = React.useCallback(() => {
    dispatch(shippingMethodsFailed({ message: "Unable to load shipping methods" }));
  }, [dispatch]);
  const { shippingMethodList, reloadShippingMethods } = useShippingMethodsList(
    () => {
    },
    onShippingMethodLoadError
  );
  const onError = React.useCallback(() => {
    restoreShippingMethod();
    reloadShippingMethods();
  }, [reloadShippingMethods, restoreShippingMethod]);
  const { handleOnShippingMethodSelected } = useShippingMethodSelection({
    shippingMethodList: shippingMethodList || [],
    onError
  });
  const onShippingMethodSelect = React.useCallback(
    (method) => {
      dispatch(
        selectShippingMethod({
          shippingMethod: {
            method,
            isValid: true,
            isTouched: true
          }
        })
      );
      switch (method) {
        case ShippingMethod.Pickup:
          if (selectedStore && selectedStore.globalStoreId) {
            handleOnShippingMethodSelected(method, selectedStore.globalStoreId);
          }
          break;
        case ShippingMethod.PickupPoint:
          if (selectedPickupPoint) {
            handleOnShippingMethodSelected(method);
          }
          break;
        default:
          handleOnShippingMethodSelected(method);
          break;
      }
    },
    [dispatch, handleOnShippingMethodSelected, selectedPickupPoint, selectedStore]
  );
  const checkStepOverviewValidity = React.useCallback(() => {
    if (!isOverviewStepValid) {
      onPrevStep(CheckoutStepErrorType.ShippingStepFailed);
    }
  }, [isOverviewStepValid, onPrevStep]);
  React.useEffect(() => {
    if (cart && called && !loading) {
      checkStepOverviewValidity();
    }
  }, [cart, called, loading]);
  const onCompleted = React.useCallback(
    (operation) => {
      cart?.checkoutState && dispatch(
        updateCartState({
          cartState: cart?.checkoutState
        })
      );
      operation === "MUTATION" && onNextStep();
    },
    [cart?.checkoutState, dispatch, onNextStep]
  );
  const { setCustomerDetails } = useSetCustomerDetails({
    onCompleted,
    onError: () => {
      toast.failed("checkout.errors.form.general");
    }
  });
  const onNextClick = () => {
    if (!isOverviewStepValid) {
      onPrevStep();
    } else if (customerDetails && !isEmpty(customerDetails)) {
      setCustomerDetails(customerDetails);
    }
  };
  const onStoreSelect = async (store) => {
    const { globalStoreId } = store;
    if (selectedShippingMethod?.method && isPickupStore$1 && !isPickupStore(cart?.shippingMethod.key)) {
      dispatch(selectStore({ store }));
      await handleOnShippingMethodSelected(selectedShippingMethod?.method, globalStoreId);
    }
    if (cart?.shippingMethod && isPickupStore(cart.shippingMethod.key) && selectedStore?.globalStoreId !== globalStoreId) {
      dispatch(selectStore({ store }));
      await handleOnShippingMethodSelected(cart?.shippingMethod.key, globalStoreId);
    }
  };
  const onPickupPointSelect = async (pickupPoint) => {
    if (!pickupPoint) {
      return;
    }
    dispatch(selectPickupPoint({ pickupPoint }));
    if (selectedShippingMethod?.method && !isPickupPoint(cart?.shippingMethod.key)) {
      await handleOnShippingMethodSelected(selectedShippingMethod?.method);
    }
  };
  const labels = React.useMemo(
    () => ({
      generic: {
        title: getLabel("checkout.generic.title"),
        deliveryAddress: getLabel("checkout.deliveryAddress")
      },
      yourDetails: getLabel("checkout.yourDetails"),
      billingAddress: getLabel("checkout.billingAddress"),
      completed: {
        shipmentDetailsDelivery: getLabel("checkout.completed.shipmentDetails.delivery")
      },
      contactTitle: getLabel("checkout.contact.title"),
      customerTypeCompany: getLabel("checkout.customerType.company"),
      serviceStoreTitle: getLabel("checkout.serviceStore.title"),
      shipmentAddress: getLabel("checkout.shipment.address"),
      summary: {
        delivery: getLabel("checkout.summary.shipment.delivery"),
        deliveryExpress: getLabel("checkout.summary.shipment.deliveryExpress"),
        pickupStore: getLabel("checkout.summary.shipment.pickup"),
        pickupPoint: getLabel("checkout.summary.shipment.pickupPoint"),
        nearestPickup: getLabel("checkout.summary.shipment.nearestPickup"),
        address: getLabel("checkout.summary.shipment.address"),
        deliveryPremium: getLabel("checkout.summary.shipment.deliveryPremium"),
        deliveryResponsible: getLabel("checkout.summary.shipment.deliveryResponsible")
      },
      editModal: {
        save: getLabel("common.actions.save")
      },
      differentDeliveryAddress: getLabel("checkout.shipment.differentDeliveryAddress"),
      nearestPickupMessage: getLabel("checkout.summary.shipment.nearestPickup.message")
    }),
    [getLabel]
  );
  const handleClose = () => {
    restoreShippingMethod();
  };
  return /* @__PURE__ */ React.createElement(
    StepOverview,
    {
      cartComponent: props.cartComponent,
      isPickupPoint: isPickupPoint$1,
      isPickupStore: isPickupStore$1,
      labels,
      onEdit: onPrevStep,
      onNextClick,
      onPrevClick: onPrevStep,
      selectedPickupPoint,
      selectedStore,
      serviceStore,
      shippingMethod: selectedShippingMethod,
      shippingMethodsAsides,
      onPickupPointSelect,
      onStoreSelect,
      shippingMethodList,
      handleOnShippingMethodSelected: onShippingMethodSelect,
      onShippingModalClose: handleClose
    }
  );
};
export {
  StepOverviewContainer
};
//# sourceMappingURL=StepOverviewContainer.js.map
